import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { ChevronLeftRounded, ChevronRightRounded } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useIsMobile } from '../../../../hooks';
import { TicketSeasonsCalendar } from './ticket-seasons-calendar';

const currentYear = dayjs().year();

const computeMonth = (month: string) => dayjs(`${currentYear}-${month}-01`, 'YYYY-MM-DD');

const january = computeMonth('01');
const february = computeMonth('02');
const march = computeMonth('03');
const april = computeMonth('04');
const may = computeMonth('05');
const june = computeMonth('06');
const july = computeMonth('07');
const august = computeMonth('08');
const september = computeMonth('09');
const october = computeMonth('10');
const november = computeMonth('11');
const december = computeMonth('12');

const months = [january, february, march, april, may, june, july, august, september, october, november, december];

export const TicketSeasons = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const [currentQuarter, setCurrentQuarter] = useState(1);

  useEffect(() => {
    setCurrentQuarter(1); /** Reset current quarter after change between desktop and mobile views */
  }, [isMobile]);

  const handleSetCurrentQuarter = (quarter: number) => {
    if (!isMobile && (quarter < 1 || quarter > 4)) return;
    if (isMobile && (quarter < 1 || quarter > 12)) return;

    setCurrentQuarter(quarter);
  };

  return (
    <Stack>
      <Stack flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={3} mb={3.5}>
        <Typography variant="h2" color="primary.main" fontFamily="Gotham-Medium" fontSize={32} fontWeight={500}>
          {t('tickets_service.ticket_seasons_calendar.title')} {currentYear}
        </Typography>

        <Stack flexDirection="row" gap={3.5}>
          <IconButton
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 48,
              height: 48,
              borderRadius: 24,
              borderStyle: 'solid',
              borderWidth: 1,
              borderColor: 'primary.main',
              color: 'primary.main',
            }}
            onClick={() => handleSetCurrentQuarter(currentQuarter - 1)}
          >
            <ChevronLeftRounded />
          </IconButton>
          <IconButton
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 48,
              height: 48,
              borderRadius: 24,
              borderStyle: 'solid',
              borderWidth: 1,
              borderColor: 'primary.main',
              color: 'primary.main',
            }}
            onClick={() => handleSetCurrentQuarter(currentQuarter + 1)}
          >
            <ChevronRightRounded />
          </IconButton>
        </Stack>
      </Stack>

      <Box
        display="grid"
        gridTemplateColumns={{ xs: 'repeat(1, 1fr)', md: 'repeat(3, 1fr)' }}
        gap={3}
        justifyContent="center"
      >
        {isMobile ? (
          <TicketSeasonsCalendar year={months[currentQuarter - 1].year()} month={months[currentQuarter - 1].month()} />
        ) : (
          months
            .filter((_, index) => index + 1 >= currentQuarter * 3 - 2 && index + 1 <= currentQuarter * 3)
            .map((month) => <TicketSeasonsCalendar year={month.year()} month={month.month()} />)
        )}
      </Box>

      <Stack flexDirection="row" justifyContent={{ xs: 'center', md: 'flex-end' }} gap={4} mt={3}>
        <Stack width="fit-content">
          <Box width="100%" height={32} bgcolor="#D9D9D9" borderRadius={0.5} />
          <Typography textAlign="center">{t('tickets_service.seasons.low')}</Typography>
        </Stack>
        <Stack width="fit-content">
          <Box width="100%" height={32} bgcolor="primary.main" borderRadius={0.5} />
          <Typography textAlign="center">{t('tickets_service.seasons.regular')}</Typography>
        </Stack>
        <Stack width="fit-content">
          <Box width="100%" height={32} bgcolor="primary.light" borderRadius={0.5} />
          <Typography textAlign="center">{t('tickets_service.seasons.high')}</Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
